<template lang="pug">
    .drawer-container
        el-button.action(
            type="primary"
            icon="el-icon-fa-envelope fas"
            :disabled="!hasTemplates"
            @click="open"
            style="display: none;"
        ) {{ $t('Create Newsletter') }}

        el-drawer.create-newsletter-drawer(
            :title="$t('Send Newsletter')"
            :visible.sync="visible"
            :modal="true"
            size="50%"
            @close="onClose"
            @closed="onClosed"
        )
            .drawer-content(v-if="visible")
                .drawer-body.scroll-box(v-dragscroll)
                    //- Warning banner when no articles
                    .warning-banner(v-if="!hasArticles")
                        i.el-icon-fa-exclamation-triangle.fas
                        span {{ $t('No articles scanned. Please scan articles before sending a newsletter.') }}

                    //- Info banner
                    .info-banner(v-else)
                        i.el-icon-fa-info-circle.fas
                        span {{ $t('The scanned articles on the left will be included in the newsletter.') }}

                    //- Template selector
                    .form-section
                        .section-label {{ $t('Template') }}
                        el-select(
                            v-model="selectedTemplateId"
                            :placeholder="$t('Select a template')"
                            :disabled="templates.length <= 1"
                            size="medium"
                            style="width: 100%"
                        )
                            el-option(
                                v-for="template in templates"
                                :key="template.id"
                                :label="template.subject"
                                :value="template.id"
                            )

                    //- Subject
                    .form-section
                        .section-label {{ $t('Subject') }}
                        .input-with-keyboard
                            el-input(
                                ref="subjectInput"
                                v-model="subject"
                                :placeholder="$t('Enter subject')"
                                size="medium"
                            )
                            button.keyboard-toggle(
                                type="button"
                                :class="{ active: showSubjectKeyboard }"
                                @click.prevent.stop="toggleSubjectKeyboard"
                            )
                                i.el-icon-fa-keyboard.far
                        transition(name="slide-decent-vertical")
                            .keyboard-container(v-if="showSubjectKeyboard")
                                Keyboard(
                                    v-model="subject"
                                    @key-press="handleSubjectKeyboardKeyPress"
                                )

                    //- Preheader
                    .form-section
                        .section-label {{ $t('Preheader') }}
                        .section-hint {{ $t('Short preview text shown in email clients.') }}
                        .input-with-keyboard
                            el-input(
                                ref="preheaderInput"
                                v-model="preheader"
                                :placeholder="$t('Enter preheader')"
                                size="medium"
                            )
                            button.keyboard-toggle(
                                type="button"
                                :class="{ active: showPreheaderKeyboard }"
                                @click.prevent.stop="togglePreheaderKeyboard"
                            )
                                i.el-icon-fa-keyboard.far
                        transition(name="slide-decent-vertical")
                            .keyboard-container(v-if="showPreheaderKeyboard")
                                Keyboard(
                                    v-model="preheader"
                                    @key-press="handlePreheaderKeyboardKeyPress"
                                )

                    //- Test mode toggle
                    .form-section
                        .section-label
                            el-switch(
                                v-model="testMode"
                                :active-text="$t('Test Mode')"
                            )

                    //- Test mode fields
                    template(v-if="testMode")
                        .form-section
                            .section-label {{ $t('Test Email Address') }}
                            .input-with-keyboard
                                el-input(
                                    ref="testEmailInput"
                                    v-model="testEmail"
                                    :placeholder="$t('Enter email address')"
                                    type="email"
                                    size="medium"
                                )
                                button.keyboard-toggle(
                                    type="button"
                                    :class="{ active: showTestEmailKeyboard }"
                                    @click.prevent.stop="toggleTestEmailKeyboard"
                                )
                                    i.el-icon-fa-keyboard.far
                            transition(name="slide-decent-vertical")
                                .keyboard-container(v-if="showTestEmailKeyboard")
                                    Keyboard(
                                        v-model="testEmail"
                                        @key-press="handleTestEmailKeyboardKeyPress"
                                    )

                        .form-section
                            .section-label {{ $t('Personalization Member') }}
                            .section-hint {{ $t('Optional: Select a member for Twig personalization (name, salutation, etc.)') }}
                            member-search(
                                v-model="testMember"
                                :placeholder="$t('Search member...')"
                                :clearable="true"
                                :show-details="true"
                            )

                    //- Production mode: Recipient selection
                    template(v-else)
                        .form-section
                            .section-label {{ $t('Recipients') }}
                            member-search(
                                ref="memberSearch"
                                :placeholder="$t('Search and add recipients...')"
                                :show-details="true"
                                :exclude-ids="selectedRecipientIds"
                                @select="addRecipient"
                            )

                        .selected-recipients(v-if="selectedRecipients.length")
                            .section-label {{ $t('Selected Recipients') }} ({{ selectedRecipients.length }})
                            .recipients-list
                                .recipient-chip(
                                    v-for="recipient in selectedRecipients"
                                    :key="recipient.id"
                                )
                                    .recipient-info
                                        .name-row
                                            span.nr(v-if="getRecipientNr(recipient)") \#{{ getRecipientNr(recipient) }}
                                            span.salutation(v-if="recipient.gender") {{ formatGender(recipient.gender) }}
                                            span.name {{ recipient.first_name }} {{ recipient.last_name }}
                                        span.email {{ recipient.email }}
                                        .tags(v-if="recipient.memberTags && recipient.memberTags.length")
                                            span.tag(v-for="tag in recipient.memberTags.slice(0, 3)" :key="tag.id") {{ tag.name }}
                                    el-button(
                                        type="text"
                                        icon="el-icon-fa-times fas"
                                        size="mini"
                                        @click="removeRecipient(recipient.id)"
                                    )

                .drawer-footer
                    el-button(
                        size="large"
                        icon="el-icon-fa-times fas"
                        @click="close"
                    ) {{ $t('Cancel') }}
                    el-button(
                        size="large"
                        type="primary"
                        icon="el-icon-fa-paper-plane fas"
                        :disabled="!canSend"
                        :loading="sending"
                        @click="send"
                    ) {{ testMode ? $t('Send Test') : $t('Send Newsletter') }}
</template>

<i18n>
{
    "en": {
        "Test newsletter sent to {email}": "Test newsletter sent to {email}",
        "pos-template": "POS Template"
    },
    "de": {
        "Send Newsletter": "Newsletter senden",
        "No articles scanned. Please scan articles before sending a newsletter.": "Keine Artikel gescannt. Bitte scannen Sie Artikel, bevor Sie einen Newsletter senden.",
        "The scanned articles on the left will be included in the newsletter.": "Die gescannten Artikel links werden in den Newsletter aufgenommen.",
        "Template": "Vorlage",
        "Select a template": "Vorlage auswählen",
        "Test Mode": "Testmodus",
        "Test Email Address": "Test-E-Mail-Adresse",
        "Enter email address": "E-Mail-Adresse eingeben",
        "Personalization Member": "Personalisierungs-Mitglied",
        "Optional: Select a member for Twig personalization (name, salutation, etc.)": "Optional: Wählen Sie ein Mitglied für die Twig-Personalisierung (Name, Anrede, etc.)",
        "Search member...": "Mitglied suchen...",
        "Recipients": "Empfänger",
        "Search and add recipients...": "Empfänger suchen und hinzufügen...",
        "Selected Recipients": "Ausgewählte Empfänger",
        "Cancel": "Abbrechen",
        "Send Test": "Test senden",
        "Test Sent": "Test gesendet",
        "Test newsletter sent to {email}": "Test-Newsletter an {email} gesendet",
        "Send Newsletter": "Newsletter senden",
        "Create Newsletter": "Newsletter erstellen",
        "Subject": "Betreff",
        "Enter subject": "Betreff eingeben",
        "Preheader": "Vorschautext",
        "Enter preheader": "Vorschautext eingeben",
        "Short preview text shown in email clients.": "Kurzer Vorschautext, der in E-Mail-Clients angezeigt wird."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { usePosNewsletterStore, usePosCartStore, usePosSetupStore, useAuthStore } from '@/stores';
import MemberSearch from './MemberSearch';
import Keyboard from '@/components/Keyboard';

export default {
    name: 'CreateNewsletterDrawer',
    components: {
        MemberSearch,
        Keyboard,
    },
    data() {
        return {
            visible: false,
            selectedTemplateId: null,
            subject: '',
            preheader: '',
            testMode: false,
            testEmail: '',
            testMember: null,
            focusedField: null,
            showSubjectKeyboard: false,
            showPreheaderKeyboard: false,
            showTestEmailKeyboard: false,
        };
    },
    computed: {
        posNewsletterStore() {
            return usePosNewsletterStore();
        },
        posCartStore() {
            return usePosCartStore();
        },
        posSetupStore() {
            return usePosSetupStore();
        },
        authStore() {
            return useAuthStore();
        },
        templates() {
            return this.posNewsletterStore.templates.items;
        },
        hasTemplates() {
            return this.posNewsletterStore.hasTemplates;
        },
        selectedRecipients() {
            return this.posNewsletterStore.selectedRecipients;
        },
        selectedRecipientIds() {
            return this.selectedRecipients.map(r => r.id);
        },
        sending() {
            return this.posNewsletterStore.sending;
        },
        hasArticles() {
            return this.cartArticleIds.length > 0;
        },
        canSend() {
            if (!this.hasArticles || !this.selectedTemplateId) {
                return false;
            }
            if (this.testMode) {
                return !!this.testEmail;
            }
            return this.selectedRecipients.length > 0;
        },
        cartArticleIds() {
            const items = this.posCartStore.items || [];
            return items.map(item => item.article && item.article.id).filter(Boolean);
        }
    },
    watch: {
        templates: {
            handler(templates) {
                if (templates && templates.length > 0 && !this.selectedTemplateId) {
                    this.selectedTemplateId = templates[0].id;
                }
            },
            immediate: true
        },
        selectedTemplateId(id) {
            if (id) {
                const template = this.templates.find(t => t.id === id);
                this.posNewsletterStore.setSelectedTemplate(template);
                // Pre-fill subject and preheader from template
                if (template) {
                    this.subject = _.get(template, 'subject', '') || '';
                    this.preheader = _.get(template, 'preheader', '') || '';
                }
            }
        },
        subject(value) {
            this.posNewsletterStore.setSubject(value);
        },
        preheader(value) {
            this.posNewsletterStore.setPreheader(value);
        },
        testMode(value) {
            this.posNewsletterStore.setTestMode(value);
            // Reset keyboard when switching modes
            this.showTestEmailKeyboard = false;
        },
        testEmail(value) {
            this.posNewsletterStore.setTestEmail(value);
        },
        testMember(value) {
            this.posNewsletterStore.setTestMember(value);
        }
    },
    methods: {
        open() {
            this.visible = true;
            this.posNewsletterStore.reset();
            this.testMode = false;
            // Prefill test email with logged in user's email
            const userEmail = _.get(this.authStore, 'user.email', '');
            this.testEmail = userEmail;
            this.posNewsletterStore.setTestEmail(userEmail); // Explicitly sync to store
            this.testMember = null;
            this.focusedField = null;
            this.showSubjectKeyboard = false;
            this.showPreheaderKeyboard = false;
            this.showTestEmailKeyboard = false;
            if (this.templates.length > 0) {
                this.selectedTemplateId = this.templates[0].id;
            }

            // Disable autofocus on main search input while drawer is open
            this.$emit('blur-search');
            this.posSetupStore.updateAutofocusSearchInput(false);
        },
        openWithDraft(newsletter) {
            // First do the normal open (which resets state)
            this.open();

            // Then set the draft ID so subsequent sends reuse this draft
            if (newsletter && newsletter.id) {
                this.posNewsletterStore.setCurrentDraftId(newsletter.id);

                // Pre-select the source template if available
                if (newsletter.sourceNewsletter && newsletter.sourceNewsletter.id) {
                    const templateId = parseInt(newsletter.sourceNewsletter.id, 10);
                    const template = this.templates.find(t => parseInt(t.id, 10) === templateId);
                    if (template) {
                        this.selectedTemplateId = template.id;
                    }
                }

                // Override subject/preheader with draft's custom values
                // (the selectedTemplateId watcher pre-fills from template, but the draft may have custom values)
                this.subject = _.get(newsletter, 'subject', '') || '';
                this.preheader = _.get(newsletter, 'preheader', '') || '';

                // Enable test mode since this is a test draft
                this.testMode = true;
            }
        },
        close() {
            this.visible = false;
        },
        onClose() {
            this.$emit('focus-search');
        },
        onClosed() {
            // Re-enable autofocus on main search input when drawer is closed
            this.posSetupStore.updateAutofocusSearchInput(true);
            this.focusedField = null;
        },
        onInputFocus(fieldName) {
            this.focusedField = fieldName;
        },
        toggleSubjectKeyboard() {
            this.showSubjectKeyboard = !this.showSubjectKeyboard;
            this.showPreheaderKeyboard = false;
            this.showTestEmailKeyboard = false;
        },
        handleSubjectKeyboardKeyPress(button) {
            if (button === '{enter}' || button === '{return}') {
                this.showSubjectKeyboard = false;
            }
        },
        togglePreheaderKeyboard() {
            this.showPreheaderKeyboard = !this.showPreheaderKeyboard;
            this.showSubjectKeyboard = false;
            this.showTestEmailKeyboard = false;
        },
        handlePreheaderKeyboardKeyPress(button) {
            if (button === '{enter}' || button === '{return}') {
                this.showPreheaderKeyboard = false;
            }
        },
        toggleTestEmailKeyboard() {
            this.showTestEmailKeyboard = !this.showTestEmailKeyboard;
            this.showSubjectKeyboard = false;
            this.showPreheaderKeyboard = false;
        },
        handleTestEmailKeyboardKeyPress(button) {
            if (button === '{enter}' || button === '{return}') {
                this.showTestEmailKeyboard = false;
            }
        },
        getRecipientNr(recipient) {
            return _.get(recipient, 'customer.nr') || recipient.nr;
        },
        formatGender(gender) {
            const genderMap = {
                'M': 'Herr',
                'F': 'Frau',
                'X': ''
            };
            return genderMap[gender] || '';
        },
        addRecipient(member) {
            if (member) {
                this.posNewsletterStore.addRecipient(member);
            }
        },
        removeRecipient(memberId) {
            this.posNewsletterStore.removeRecipient(memberId);
        },
        async send() {
            try {
                await this.posNewsletterStore.send(this.cartArticleIds);

                if (this.testMode) {
                    // Test mode: show success, keep drawer open for real send
                    this.$notify.success({
                        title: this.$t('Test Sent'),
                        message: this.$t('Test newsletter sent to {email}', { email: this.testEmail }),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 5000
                    });
                } else {
                    // Production mode: close drawer and clear
                    this.close();
                    this.posNewsletterStore.clearRecipients();
                    this.$emit('sent');
                }
            } catch (error) {
                console.error('Error sending newsletter:', error);
                this.$notify.error({
                    title: this.$t('Error'),
                    message: error.message || this.$t('Failed to send newsletter'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
            }
        }
    }
};
</script>

<style lang="sass" scoped>
.drawer-container
    display: contents

.create-newsletter-drawer
    &::v-deep .el-drawer__header
        margin-bottom: 0
        padding: 20px
        border-bottom: 1px solid rgba(0,0,0,0.1)
    &::v-deep .el-drawer__body
        display: flex
        flex-direction: column
        overflow: hidden

.drawer-content
    display: flex
    flex-direction: column
    height: 100%
    overflow: hidden

.drawer-body
    flex: 1
    padding: 20px
    overflow-y: auto

.warning-banner
    background: rgba($--color-warning, 0.1)
    border: 1px solid rgba($--color-warning, 0.3)
    border-radius: 4px
    padding: 12px 16px
    margin-bottom: 20px
    display: flex
    align-items: center
    gap: 10px
    i
        color: $--color-warning
        font-size: 1.2em

.info-banner
    background: rgba($--color-info, 0.1)
    border: 1px solid rgba($--color-info, 0.3)
    border-radius: 4px
    padding: 12px 16px
    margin-bottom: 20px
    display: flex
    align-items: center
    gap: 10px
    i
        color: $--color-info
        font-size: 1.2em

.form-section
    margin-bottom: 20px
    .section-label
        font-weight: 500
        margin-bottom: 8px
        display: flex
        align-items: center
        gap: 8px
    .section-hint
        font-size: 0.85em
        color: $color-grey-text
        margin-bottom: 8px

.input-with-keyboard
    position: relative
    display: flex
    align-items: center
    .el-input
        flex: 1

.keyboard-toggle
    position: absolute
    right: 8px
    top: 50%
    transform: translateY(-50%)
    background: none
    border: none
    padding: 6px 8px
    cursor: pointer
    color: $color-grey-text
    font-size: 1.1em
    z-index: 1
    transition: color 0.2s
    &:hover
        color: $--color-primary
    &.active
        color: $--color-primary

.keyboard-container
    margin-top: 8px
    background: darken($color-skeleton, 10%)
    border-radius: 8px
    padding: 12px
    position: relative
    &:before
        content: ''
        position: absolute
        top: -8px
        right: 20px
        width: 0
        height: 0
        border-left: 8px solid transparent
        border-right: 8px solid transparent
        border-bottom: 8px solid darken($color-skeleton, 10%)

.selected-recipients
    margin-top: 20px
    .section-label
        margin-bottom: 12px

.recipients-list
    display: flex
    flex-direction: column-reverse
    gap: 8px

.recipient-chip
    display: flex
    align-items: flex-start
    gap: 12px
    background: rgba(0,0,0,0.05)
    border-radius: 8px
    padding: 10px 14px
    .recipient-info
        flex: 1
        display: flex
        flex-direction: column
        gap: 2px
        .name-row
            display: flex
            align-items: center
            gap: 6px
            flex-wrap: wrap
        .nr
            font-size: 0.85em
            color: $color-grey-text
            font-weight: 500
        .salutation
            font-size: 0.85em
            color: $color-grey-text
        .name
            font-weight: 500
            font-size: 0.95em
        .email
            font-size: 0.85em
            color: $color-grey-text
        .tags
            display: flex
            gap: 4px
            flex-wrap: wrap
            margin-top: 4px
        .tag
            font-size: 0.75em
            background: rgba($--color-primary, 0.1)
            color: $--color-primary
            padding: 2px 8px
            border-radius: 10px
    .el-button
        flex-shrink: 0
        padding: 4px

.drawer-footer
    flex-shrink: 0
    padding: 16px 20px
    border-top: 1px solid rgba(0,0,0,0.1)
    display: flex
    justify-content: flex-end
    gap: 12px
    background: #fff
</style>
